import { displayUserName } from "@modules/user/deletedUser";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import debounce from "lodash/debounce";
import { format } from "date-fns";
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Calendar as CalendarIcon,
  ChevronDown,
  MoreVertical,
  X,
} from "lucide-react";

import { PageTemplate } from "@components/pageTemplate/PageTemplate";
import { JobStatusCards } from "@components/background-jobs/JobStatusCards";
import {
  PesTestFilterInput,
  PesTestSortInput,
  useErUsLazyQuery,
  useFarmsLazyQuery,
  useFieldsForFarmLazyQuery,
  useCassettesLazyQuery,
  useOrganisationsLazyQuery,
  usePesTestsQuery,
  useTroManagementOverviewQuery,
  useUpdateExcludeFromMlMutation,
} from "generated/hooks";
import { useFetchAll } from "~/hooks/useFetchAll";
import { PesTestType, SortEnumType } from "generated/hooks";
import { calculateDammChecksum } from "@util/Damm";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Checkbox } from "~/components/ui/checkbox";
import { Badge } from "~/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import {
  SelectionCombobox,
  SelectionComboboxOption,
} from "~/components/ui/selection-combobox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui/popover";
import { Calendar } from "~/components/ui/calendar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { Skeleton } from "~/components/ui/skeleton";
import { cn } from "~/lib/utils";
import { useAtom } from "jotai";
import { openedTestsAtom, isTestNew } from "~/state/openedTests";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type TestNode = NonNullable<
  NonNullable<
    NonNullable<ReturnType<typeof usePesTestsQuery>["data"]>["pesTests"]
  >["nodes"]
>[number];

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STORAGE_KEY = "pes-tests-page-size";
const PAGE_SIZE_OPTIONS = [10, 25, 50] as const;
type PageSize = (typeof PAGE_SIZE_OPTIONS)[number];

type SortKey = "utcTestTime" | "name" | "farm" | "field";

const getPageSizeFromStorage = (): PageSize => {
  const stored = localStorage.getItem(STORAGE_KEY);
  const parsed = stored ? parseInt(stored, 10) : NaN;
  if (Number.isNaN(parsed)) {
    return 10;
  }
  return PAGE_SIZE_OPTIONS.includes(parsed as PageSize)
    ? (parsed as PageSize)
    : 10;
};

const savePageSizeToStorage = (pageSize: PageSize) => {
  localStorage.setItem(STORAGE_KEY, pageSize.toString());
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

type MultiSelectOption = SelectionComboboxOption;

const arraysEqual = (a: string[], b: string[]) =>
  a.length === b.length && a.every((v, i) => v === b[i]);

const optionsEqual = (a: MultiSelectOption[], b: MultiSelectOption[]) =>
  a.length === b.length &&
  a.every((o, i) => o.value === b[i]?.value && o.label === b[i]?.label);

const parseCsvParam = (value: string | null) =>
  value?.split(",").filter(Boolean) ?? [];

const mapOptionsToValues = (options: MultiSelectOption[]) =>
  options.map((o) => o.value).filter((v): v is string => Boolean(v));

const cloneOptions = (options: MultiSelectOption[]) =>
  options.map((o) => ({ ...o }));

const buildOptions = <T,>(
  items: T[] | null | undefined,
  getValue: (item: T) => string,
  getLabel: (item: T) => string
): MultiSelectOption[] => {
  const mappedOptions =
    items?.map((item) => ({ value: getValue(item), label: getLabel(item) })) ??
    [];

  return Array.from(
    new Map(mappedOptions.map((option) => [option.value, option])).values()
  );
};

const inflateOptions = (
  ids: string[],
  previous: MultiSelectOption[],
  available: MultiSelectOption[]
) => {
  const next = ids.map(
    (id) =>
      available.find((o) => o.value === id) ??
      previous.find((o) => o.value === id) ?? { value: id, label: "" }
  );
  return optionsEqual(previous, next) ? previous : next;
};

const resolveSelectedOptions = (
  ids: string[],
  options: MultiSelectOption[],
  fallback?: (id: string) => MultiSelectOption | null
) =>
  ids
    .map((id) => options.find((o) => o.value === id) ?? fallback?.(id))
    .filter((o): o is MultiSelectOption => Boolean(o && o.value));

const getDefaultFromDate = () => new Date("2024-09-01");

const parseDateOr = (value: string | null, fallbackFactory: () => Date) => {
  if (!value) {
    return fallbackFactory();
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? fallbackFactory() : parsed;
};

// ---------------------------------------------------------------------------
// Filter Config types
// ---------------------------------------------------------------------------

type IdListFilterKey =
  | "selectedOrganisations"
  | "selectedFarms"
  | "selectedFields";
type OptionFilterKey = "selectedCassettes" | "selectedErus" | "selectedTros";

type IdListFilterConfig = {
  kind: "idList";
  key: IdListFilterKey;
  param: string;
  setter: Dispatch<SetStateAction<string[]>>;
  options: MultiSelectOption[];
  fallback?: (id: string) => MultiSelectOption | null;
};

type OptionFilterConfig = {
  kind: "optionList";
  key: OptionFilterKey;
  param: string;
  setter: Dispatch<SetStateAction<MultiSelectOption[]>>;
  options: MultiSelectOption[];
};

type FilterConfig = IdListFilterConfig | OptionFilterConfig;

type ConfigState<C extends FilterConfig> = C extends { kind: "idList" }
  ? string[]
  : MultiSelectOption[];

const resolveFilterSelections = <Config extends FilterConfig>(
  config: Config,
  state: ConfigState<Config>
): MultiSelectOption[] => {
  if (config.kind === "idList") {
    return resolveSelectedOptions(
      state as string[],
      config.options,
      config.fallback
    );
  }
  const optionState = state as MultiSelectOption[];
  return inflateOptions(
    mapOptionsToValues(optionState),
    optionState,
    config.options
  );
};

// ---------------------------------------------------------------------------
// URL search param snapshot
// ---------------------------------------------------------------------------

interface SearchParamsSnapshot {
  searchTerm: string;
  sortKey: SortKey;
  sortDirection: "ASC" | "DESC";
  page: number;
  selectedOrganisations: string[];
  selectedFarms: string[];
  selectedFields: string[];
  selectedTestType: PesTestType | null;
  selectedCassettes: MultiSelectOption[];
  selectedErus: MultiSelectOption[];
  selectedTros: MultiSelectOption[];
  fromDateSelected: boolean;
  toDateSelected: boolean;
  dateRange: { from: Date; to: Date };
}

const PES_TEST_TYPE_VALUES = new Set<PesTestType>(
  Object.values(PesTestType) as PesTestType[]
);

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

interface FilterMultiSelectProps {
  id: string;
  label: string;
  placeholder: string;
  options: SelectionComboboxOption[];
  value: MultiSelectOption[];
  onChange: (next: MultiSelectOption[]) => void;
}

const FilterMultiSelect: React.FC<FilterMultiSelectProps> = ({
  id,
  label,
  placeholder,
  options,
  value,
  onChange,
}) => {
  const selectedValues = value.map((item) => item.value);
  const availableOptions = options.filter(
    (option) => !selectedValues.includes(option.value)
  );

  const handleSelect = (selectedValue: string) => {
    if (value.some((o) => o.value === selectedValue)) {
      return;
    }
    const selectedOption = options.find((o) => o.value === selectedValue);
    if (!selectedOption) {
      return;
    }
    onChange([...value, selectedOption]);
  };

  const handleRemove = (selectedValue: string) => {
    onChange(value.filter((o) => o.value !== selectedValue));
  };

  return (
    <div className="space-y-2 w-full">
      <Label htmlFor={id} className="text-sm font-semibold text-foreground">
        {label}
      </Label>
      <SelectionCombobox
        placeholder={placeholder}
        options={availableOptions}
        emptyMessage="No results"
        onSelect={handleSelect}
        triggerClassName="h-9 w-full justify-between rounded-md border bg-background text-sm"
        contentClassName="w-[260px]"
      />
      <div className="flex flex-wrap gap-2">
        {value.length > 0 ? (
          value.map((option) => (
            <Badge
              key={option.value}
              variant="secondary"
              className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium"
            >
              <span className="max-w-[160px] truncate">{option.label}</span>
              <button
                type="button"
                className="rounded-full p-0.5 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                onClick={() => handleRemove(option.value)}
                aria-label={`Remove ${option.label}`}
              >
                <X className="size-3" aria-hidden="true" />
              </button>
            </Badge>
          ))
        ) : (
          <p className="text-xs text-muted-foreground">No selections</p>
        )}
      </div>
    </div>
  );
};

interface DatePickerFieldProps {
  id: string;
  label: string;
  selectedDate: Date;
  placeholder: string;
  isActive: boolean;
  onSelect: (date: Date) => void;
  onClear: () => void;
}

const DatePickerField: React.FC<DatePickerFieldProps> = ({
  id,
  label,
  selectedDate,
  placeholder,
  isActive,
  onSelect,
  onClear,
}) => (
  <div className="space-y-2">
    <Label htmlFor={id} className="text-sm font-semibold text-foreground">
      {label}
    </Label>
    <div className="flex items-center gap-2">
      <Popover>
        <PopoverTrigger>
          <Button
            id={id}
            variant="outline"
            className={cn(
              "inline-flex h-9 w-full items-center justify-start gap-2 rounded-md border bg-background text-left text-sm font-normal",
              !isActive && "text-muted-foreground"
            )}
          >
            <CalendarIcon className="size-4" />
            <span>
              {isActive ? format(selectedDate, "dd/MM/yyyy") : placeholder}
            </span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={(d) => {
              if (d) {
                onSelect(d);
              }
            }}
            initialFocus
          />
        </PopoverContent>
      </Popover>
      {isActive && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="px-2 text-xs text-muted-foreground hover:text-foreground"
          onClick={onClear}
        >
          Clear
        </Button>
      )}
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Build sort input for the new API
// ---------------------------------------------------------------------------

const buildSortInput = (
  key: SortKey,
  direction: "ASC" | "DESC"
): PesTestSortInput => {
  const dir = direction === "ASC" ? SortEnumType.Asc : SortEnumType.Desc;
  switch (key) {
    case "utcTestTime":
      return { utcTestTime: dir };
    case "name":
      return { name: dir };
    case "farm":
      return { farm: { name: dir } } as unknown as PesTestSortInput;
    case "field":
      return { field: { name: dir } } as unknown as PesTestSortInput;
  }
};

// ---------------------------------------------------------------------------
// Build where input for the new API
// ---------------------------------------------------------------------------

const buildWhereInput = ({
  searchTerm,
  selectedOrganisations,
  selectedFarms,
  selectedFields,
  selectedCassettes,
  selectedErus,
  selectedTros,
  selectedTestType,
  dateRange,
  fromDateSelected,
  toDateSelected,
}: {
  searchTerm: string;
  selectedOrganisations: string[];
  selectedFarms: string[];
  selectedFields: string[];
  selectedCassettes: MultiSelectOption[];
  selectedErus: MultiSelectOption[];
  selectedTros: MultiSelectOption[];
  selectedTestType: PesTestType | null;
  dateRange: { from: Date; to: Date };
  fromDateSelected: boolean;
  toDateSelected: boolean;
}): PesTestFilterInput | undefined => {
  const filters: PesTestFilterInput[] = [];

  if (searchTerm) {
    filters.push({ name: { contains: searchTerm } });
  }

  if (selectedOrganisations.length > 0) {
    filters.push(
      { organisation: { id: { in: selectedOrganisations } } } as unknown as PesTestFilterInput
    );
  }

  if (selectedFarms.length > 0) {
    filters.push({ farm: { id: { in: selectedFarms } } } as unknown as PesTestFilterInput);
  }

  if (selectedFields.length > 0) {
    filters.push({ field: { id: { in: selectedFields } } } as unknown as PesTestFilterInput);
  }

  const cassetteIds = mapOptionsToValues(selectedCassettes);
  if (cassetteIds.length > 0) {
    filters.push({ cassette: { id: { in: cassetteIds } } } as unknown as PesTestFilterInput);
  }

  const eruIds = mapOptionsToValues(selectedErus);
  if (eruIds.length > 0) {
    filters.push({ eru: { id: { in: eruIds } } } as unknown as PesTestFilterInput);
  }

  const troUuids = mapOptionsToValues(selectedTros);
  if (troUuids.length > 0) {
    filters.push({ troUuid: { in: troUuids } });
  }

  if (selectedTestType) {
    filters.push({ testType: { eq: selectedTestType } });
  }

  const dateFilter: PesTestFilterInput = {};
  if (fromDateSelected) {
    dateFilter.createdAt = {
      ...dateFilter.createdAt,
      gte: new Date(
        `${dateRange.from.toISOString().split("T")[0]}T00:00:00.000Z`
      ).toISOString(),
    };
  }
  if (toDateSelected) {
    dateFilter.createdAt = {
      ...dateFilter.createdAt,
      lte: new Date(
        `${dateRange.to.toISOString().split("T")[0]}T23:59:59.999Z`
      ).toISOString(),
    };
  }
  if (dateFilter.createdAt) {
    filters.push(dateFilter);
  }

  if (filters.length === 0) {
    return undefined;
  }
  if (filters.length === 1) {
    return filters[0];
  }
  return { and: filters };
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const PesTests = () => {
  const navigate = useNavigate();
  const [openedTests, setOpenedTests] = useAtom(openedTestsAtom);
  const [updateExcludeFromML] = useUpdateExcludeFromMlMutation();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSearchTerm = searchParams.get("search") ?? "";
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
  const [debouncedSearchTerm, setDebouncedSearchTerm] =
    useState(initialSearchTerm);
  const [pageSize, setPageSize] = useState<PageSize>(getPageSizeFromStorage);

  // -- Sort state --
  const initialSortKey = (searchParams.get("sort") ?? "utcTestTime") as SortKey;
  const initialSortDirection =
    (searchParams.get("direction") as "ASC" | "DESC") ?? "DESC";
  const [sortKey, setSortKey] = useState<SortKey>(initialSortKey);
  const [sortDirection, setSortDirection] = useState<"ASC" | "DESC">(
    initialSortDirection
  );

  // -- Filter state --
  const initialOrganisations = parseCsvParam(searchParams.get("organisations"));
  const initialFarms = parseCsvParam(searchParams.get("farms"));
  const initialFields = parseCsvParam(searchParams.get("fields"));
  const initialTestType = (searchParams.get("testType") as PesTestType) || null;
  const initialCassettes = parseCsvParam(searchParams.get("cassettes"));
  const initialErus = parseCsvParam(searchParams.get("erus"));
  const initialTros = parseCsvParam(searchParams.get("tros"));

  const [selectedOrganisations, setSelectedOrganisations] =
    useState<string[]>(initialOrganisations);
  const [selectedFarms, setSelectedFarms] = useState<string[]>(initialFarms);
  const [selectedFields, setSelectedFields] = useState<string[]>(initialFields);
  const [selectedTestType, setSelectedTestType] = useState<PesTestType | null>(
    initialTestType
  );
  const [selectedCassettes, setSelectedCassettes] = useState<
    MultiSelectOption[]
  >(initialCassettes.map((id) => ({ value: id, label: "" })));
  const [selectedErus, setSelectedErus] = useState<MultiSelectOption[]>(
    initialErus.map((id) => ({ value: id, label: "" }))
  );
  const [selectedTros, setSelectedTros] = useState<MultiSelectOption[]>(
    initialTros.map((uuid) => ({ value: uuid, label: "" }))
  );

  // -- Date state --
  const urlFromDate = searchParams.get("fromDate");
  const urlToDate = searchParams.get("toDate");
  const [fromDateSelected, setFromDateSelected] = useState(
    Boolean(urlFromDate)
  );
  const [toDateSelected, setToDateSelected] = useState(Boolean(urlToDate));
  const [dateRange, setDateRange] = useState<{ from: Date; to: Date }>(() => ({
    from: parseDateOr(urlFromDate, getDefaultFromDate),
    to: parseDateOr(urlToDate, () => new Date()),
  }));
  const defaultToDateRef = useRef<Date>(new Date(dateRange.to));

  // -- UI state --
  const [collapsed, setCollapsed] = useState(true);
  const [selectedTests, setSelectedTests] = useState<TestNode[]>([]);

  // -- Cursor-based pagination --
  const [currentPage, setCurrentPage] = useState(1);
  const [cursorStack, setCursorStack] = useState<string[]>([]);

  const currentCursor =
    cursorStack.length > 0 ? cursorStack[cursorStack.length - 1] : undefined;

  // ---------------------------------------------------------------------------
  // URL search params sync
  // ---------------------------------------------------------------------------

  const updateSearchParams = useCallback(
    (overrides?: Partial<SearchParamsSnapshot>) => {
      const {
        searchTerm: oSearch,
        sortKey: oSortKey,
        sortDirection: oSortDir,
        page: oPage,
        selectedOrganisations: oOrgs,
        selectedFarms: oFarms,
        selectedFields: oFields,
        selectedTestType: oTestType,
        selectedCassettes: oCassettes,
        selectedErus: oErus,
        selectedTros: oTros,
        fromDateSelected: oFromDateSel,
        toDateSelected: oToDateSel,
        dateRange: oDateRange,
      } = overrides ?? {};

      const nextSearch = oSearch !== undefined ? oSearch : searchTerm;
      const nextSortKey = oSortKey !== undefined ? oSortKey : sortKey;
      const nextSortDir = oSortDir !== undefined ? oSortDir : sortDirection;
      const nextOrgs = oOrgs !== undefined ? oOrgs : selectedOrganisations;
      const nextFarms = oFarms !== undefined ? oFarms : selectedFarms;
      const nextFields = oFields !== undefined ? oFields : selectedFields;
      const nextTestType =
        oTestType !== undefined ? oTestType : selectedTestType;
      const nextCassettes =
        oCassettes !== undefined ? oCassettes : selectedCassettes;
      const nextErus = oErus !== undefined ? oErus : selectedErus;
      const nextTros = oTros !== undefined ? oTros : selectedTros;
      const nextFromDateSel =
        oFromDateSel !== undefined ? oFromDateSel : fromDateSelected;
      const nextToDateSel =
        oToDateSel !== undefined ? oToDateSel : toDateSelected;
      const nextDateRange = oDateRange !== undefined ? oDateRange : dateRange;

      const params = new URLSearchParams(searchParams);
      params.set("sort", nextSortKey);
      params.set("direction", nextSortDir);

      if (nextSearch) {
        params.set("search", nextSearch);
      } else {
        params.delete("search");
      }

      if (nextOrgs.length > 0) {
        params.set("organisations", nextOrgs.join(","));
      } else {
        params.delete("organisations");
      }

      if (nextFarms.length > 0) {
        params.set("farms", nextFarms.join(","));
      } else {
        params.delete("farms");
      }

      if (nextFields.length > 0) {
        params.set("fields", nextFields.join(","));
      } else {
        params.delete("fields");
      }

      if (nextTestType) {
        params.set("testType", nextTestType);
      } else {
        params.delete("testType");
      }

      const cassetteValues = nextCassettes.map((c) => c.value).filter(Boolean);
      if (cassetteValues.length > 0) {
        params.set("cassettes", cassetteValues.join(","));
      } else {
        params.delete("cassettes");
      }

      const eruValues = nextErus.map((e) => e.value).filter(Boolean);
      if (eruValues.length > 0) {
        params.set("erus", eruValues.join(","));
      } else {
        params.delete("erus");
      }

      const troValues = nextTros.map((tro) => tro.value).filter(Boolean);
      if (troValues.length > 0) {
        params.set("tros", troValues.join(","));
      } else {
        params.delete("tros");
      }

      if (nextFromDateSel && nextDateRange) {
        params.set("fromDate", format(nextDateRange.from, "yyyy-MM-dd"));
      } else {
        params.delete("fromDate");
      }

      if (nextToDateSel && nextDateRange) {
        params.set("toDate", format(nextDateRange.to, "yyyy-MM-dd"));
      } else {
        params.delete("toDate");
      }

      // Remove page param since we use cursor-based pagination
      params.delete("page");

      if (params.toString() === searchParams.toString()) {
        return;
      }
      setSearchParams(params);
    },
    [
      searchParams,
      searchTerm,
      sortKey,
      sortDirection,
      selectedOrganisations,
      selectedFarms,
      selectedFields,
      selectedTestType,
      selectedCassettes,
      selectedErus,
      selectedTros,
      fromDateSelected,
      toDateSelected,
      dateRange,
      setSearchParams,
    ]
  );

  // ---------------------------------------------------------------------------
  // Data queries
  // ---------------------------------------------------------------------------

  const updateSearchParamsRef = useRef(updateSearchParams);
  updateSearchParamsRef.current = updateSearchParams;

  const debouncedUpdateSearchParam = useMemo(
    () =>
      debounce((value: string) => {
        updateSearchParamsRef.current({ searchTerm: value });
      }, 500),
    []
  );

  // Debounce the search term so the query doesn't fire on every keystroke
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const [fetchOrganisations] = useOrganisationsLazyQuery();
  const [fetchFarms] = useFarmsLazyQuery();
  const [fetchFieldsForFarm] = useFieldsForFarmLazyQuery();
  const [fetchCassettes] = useCassettesLazyQuery();
  const [fetchErus] = useErUsLazyQuery();
  const { data: troManagementData } = useTroManagementOverviewQuery({
    fetchPolicy: "cache-and-network",
  });

  const { data: allOrganisations } = useFetchAll(
    fetchOrganisations,
    (d) => d.organisations,
    { variables: { after: null } },
  );
  const { data: allFarms } = useFetchAll(fetchFarms, (d) => d.farms, {
    variables: { after: null },
  });
  const { data: allCassettes } = useFetchAll(fetchCassettes, (d) => d.cassettes, {
    variables: { after: null },
  });
  const { data: allErus } = useFetchAll(fetchErus, (d) => d.erus, {
    variables: { after: null },
  });

  // Fetch fields only when farms are selected, scoped to those farms.
  const [allFields, setAllFields] = useState<Array<{ id: string; name: string; farm?: { id: string } | null }>>([]);
  const selectedFarmsKey = useMemo(() => selectedFarms.slice().sort().join(","), [selectedFarms]);

  useEffect(() => {
    if (selectedFarms.length === 0) {
      setAllFields([]);
      return;
    }

    let cancelled = false;

    const fetchFields = async () => {
      const accumulated: Array<{ id: string; name: string; farm?: { id: string } | null }> = [];

      for (const farmId of selectedFarms) {
        let after: string | undefined;
        while (true) {
          const { data } = await fetchFieldsForFarm({
            variables: { farmId, after },
            fetchPolicy: "cache-first",
          });
          if (cancelled) {
            return;
          }

          const connection = data?.farm?.fields;
          const nodes = connection?.nodes?.filter(Boolean) ?? [];
          accumulated.push(
            ...nodes.map((n) => ({ id: n!.id, name: n!.name, farm: { id: farmId } }))
          );

          if (!connection?.pageInfo?.hasNextPage || !connection.pageInfo.endCursor) {
            break;
          }
          after = connection.pageInfo.endCursor;
        }
      }

      if (!cancelled) {
        setAllFields(accumulated);
      }
    };

    void fetchFields();
    return () => {
      cancelled = true;
    };
  }, [selectedFarmsKey, fetchFieldsForFarm]);

  const whereInput = useMemo(
    () =>
      buildWhereInput({
        searchTerm: debouncedSearchTerm,
        selectedOrganisations,
        selectedFarms,
        selectedFields,
        selectedCassettes,
        selectedErus,
        selectedTros,
        selectedTestType,
        dateRange,
        fromDateSelected,
        toDateSelected,
      }),
    [
      debouncedSearchTerm,
      selectedOrganisations,
      selectedFarms,
      selectedFields,
      selectedCassettes,
      selectedErus,
      selectedTros,
      selectedTestType,
      dateRange,
      fromDateSelected,
      toDateSelected,
    ]
  );

  const orderInput = useMemo(
    () => [buildSortInput(sortKey, sortDirection)],
    [sortKey, sortDirection]
  );

  const { data, loading, error } = usePesTestsQuery({
    variables: {
      first: pageSize,
      after: currentCursor,
      where: whereInput,
      order: orderInput,
    },
    fetchPolicy: "cache-and-network",
  });

  const tests = data?.pesTests?.nodes ?? [];
  const pageInfo = data?.pesTests?.pageInfo;
  const hasNextPage = pageInfo?.hasNextPage ?? false;
  const hasPreviousPage = currentPage > 1;

  // ---------------------------------------------------------------------------
  // Options for filter comboboxes
  // ---------------------------------------------------------------------------

  const organisationOptions = useMemo(
    () =>
      buildOptions(
        allOrganisations,
        (o) => o.id,
        (o) => o.name
      ),
    [allOrganisations]
  );

  const farmOptions = useMemo(
    () =>
      buildOptions(
        allFarms,
        (f) => f.id,
        (f) => f.name
      ),
    [allFarms]
  );

  const fieldOptions = useMemo(
    () =>
      buildOptions(
        allFields,
        (f) => f.id,
        (f) => f.name
      ),
    [allFields]
  );

  const cassetteOptions = useMemo(
    () =>
      buildOptions(
        allCassettes,
        (c) => c.id,
        (c) => {
          const code = c.serialNumber?.range?.productType?.code ?? "";
          const serial = (c.serialNumber?.serialNumber ?? 0)
            .toString()
            .padStart(7, "0");
          return `${code}${serial}${calculateDammChecksum(`${code}${serial}`)}`;
        }
      ),
    [allCassettes]
  );

  const eruOptions = useMemo(
    () =>
      buildOptions(
        allErus,
        (e) => e.id,
        (e) => e.name ?? "(No name)"
      ),
    [allErus]
  );

  const troOptions = useMemo(
    () =>
      buildOptions(
        troManagementData?.troManagementOverview.tros.filter(
          (tro) => tro.name.trim().length > 0
        ),
        (tro) => tro.uuid,
        (tro) => tro.name
      ),
    [troManagementData]
  );

  // ---------------------------------------------------------------------------
  // Filter config
  // ---------------------------------------------------------------------------

  const filterConfigs = useMemo<FilterConfig[]>(
    () => [
      {
        kind: "idList",
        key: "selectedOrganisations",
        param: "organisations",
        setter: setSelectedOrganisations,
        options: organisationOptions,
        fallback: (id: string) => ({ value: id, label: id }),
      },
      {
        kind: "idList",
        key: "selectedFarms",
        param: "farms",
        setter: setSelectedFarms,
        options: farmOptions,
      },
      {
        kind: "idList",
        key: "selectedFields",
        param: "fields",
        setter: setSelectedFields,
        options: fieldOptions,
      },
      {
        kind: "optionList",
        key: "selectedCassettes",
        param: "cassettes",
        setter: setSelectedCassettes,
        options: cassetteOptions,
      },
      {
        kind: "optionList",
        key: "selectedErus",
        param: "erus",
        setter: setSelectedErus,
        options: eruOptions,
      },
      {
        kind: "optionList",
        key: "selectedTros",
        param: "tros",
        setter: setSelectedTros,
        options: troOptions,
      },
    ],
    [
      organisationOptions,
      farmOptions,
      fieldOptions,
      cassetteOptions,
      eruOptions,
      troOptions,
    ]
  );

  const getFilterConfig = <K extends FilterConfig["key"]>(
    key: K
  ): Extract<FilterConfig, { key: K }> => {
    const config = filterConfigs.find((item) => item.key === key);
    if (!config) {
      throw new Error(`Missing filter configuration for ${key}`);
    }
    return config as Extract<FilterConfig, { key: K }>;
  };

  const createListFilterHandler = <Config extends FilterConfig>(
    config: Config
  ) => {
    return (selectedOptions: MultiSelectOption[]) => {
      const nextState = (
        config.kind === "idList"
          ? mapOptionsToValues(selectedOptions)
          : cloneOptions(selectedOptions)
      ) as ConfigState<Config>;

      (config.setter as Dispatch<SetStateAction<ConfigState<Config>>>)(
        nextState
      );
      resetPagination();
      updateSearchParams({
        [config.key]: nextState,
      } as Partial<SearchParamsSnapshot>);

    };
  };

  // Resolve filter selections
  const organisationFilterConfig = getFilterConfig("selectedOrganisations");
  const farmFilterConfig = getFilterConfig("selectedFarms");
  const fieldFilterConfig = getFilterConfig("selectedFields");
  const cassetteFilterConfig = getFilterConfig("selectedCassettes");
  const eruFilterConfig = getFilterConfig("selectedErus");
  const troFilterConfig = getFilterConfig("selectedTros");

  const handleOrganisationChange = createListFilterHandler(
    organisationFilterConfig
  );
  const handleFarmChange = createListFilterHandler(farmFilterConfig);
  const handleFieldChange = createListFilterHandler(fieldFilterConfig);
  const handleCassetteChange = createListFilterHandler(cassetteFilterConfig);
  const handleEruChange = createListFilterHandler(eruFilterConfig);
  const handleTroChange = createListFilterHandler(troFilterConfig);

  // @ts-expect-error -- generic config state mismatch
  const organisationSelections = resolveFilterSelections(organisationFilterConfig, selectedOrganisations);
  // @ts-expect-error -- generic config state mismatch
  const farmSelections = resolveFilterSelections(farmFilterConfig, selectedFarms);
  // @ts-expect-error -- generic config state mismatch
  const fieldSelections = resolveFilterSelections(fieldFilterConfig, selectedFields);
  // @ts-expect-error -- generic config state mismatch
  const cassetteSelections = resolveFilterSelections(cassetteFilterConfig, selectedCassettes);
  // @ts-expect-error -- generic config state mismatch
  const eruSelections = resolveFilterSelections(eruFilterConfig, selectedErus);
  // @ts-expect-error -- generic config state mismatch
  const troSelections = resolveFilterSelections(troFilterConfig, selectedTros);

  // ---------------------------------------------------------------------------
  // URL sync effect
  // ---------------------------------------------------------------------------

  useEffect(() => {
    const nextSearch = searchParams.get("search") ?? "";
    setSearchTerm((prev) => (prev === nextSearch ? prev : nextSearch));

    const rawSortKey = (searchParams.get("sort") ?? "utcTestTime") as SortKey;
    if (rawSortKey !== sortKey) {
      setSortKey(rawSortKey);
    }

    const rawDirection =
      (searchParams.get("direction") as "ASC" | "DESC") ?? "DESC";
    if (rawDirection !== sortDirection) {
      setSortDirection(rawDirection);
    }

    filterConfigs
      .filter((c): c is IdListFilterConfig => c.kind === "idList")
      .forEach((config) => {
        const nextIds = parseCsvParam(searchParams.get(config.param));
        config.setter((prev) => (arraysEqual(prev, nextIds) ? prev : nextIds));
      });

    filterConfigs
      .filter((c): c is OptionFilterConfig => c.kind === "optionList")
      .forEach((config) => {
        const ids = parseCsvParam(searchParams.get(config.param));
        config.setter((prev) => inflateOptions(ids, prev, config.options));
      });

    const testTypeParam = searchParams.get("testType");
    const validTestType =
      testTypeParam && PES_TEST_TYPE_VALUES.has(testTypeParam as PesTestType)
        ? (testTypeParam as PesTestType)
        : null;
    setSelectedTestType((prev) =>
      prev === validTestType ? prev : validTestType
    );

    const fromDateParam = searchParams.get("fromDate");
    const toDateParam = searchParams.get("toDate");
    const hasFromDate = Boolean(fromDateParam);
    const hasToDate = Boolean(toDateParam);

    setFromDateSelected((prev) => (prev === hasFromDate ? prev : hasFromDate));
    setToDateSelected((prev) => (prev === hasToDate ? prev : hasToDate));

    setDateRange((prev) => {
      let nextFrom = prev.from;
      if (hasFromDate && fromDateParam) {
        const parsed = new Date(fromDateParam);
        nextFrom = Number.isNaN(parsed.getTime())
          ? getDefaultFromDate()
          : parsed;
      } else if (!hasFromDate) {
        nextFrom = getDefaultFromDate();
      }

      let nextTo = prev.to;
      if (hasToDate && toDateParam) {
        const parsed = new Date(toDateParam);
        nextTo = Number.isNaN(parsed.getTime())
          ? new Date(defaultToDateRef.current)
          : parsed;
      } else if (!hasToDate) {
        nextTo = new Date(defaultToDateRef.current);
      }

      if (
        prev.from.getTime() === nextFrom.getTime() &&
        prev.to.getTime() === nextTo.getTime()
      ) {
        return prev;
      }

      return { from: nextFrom, to: nextTo };
    });
  }, [searchParams, sortKey, sortDirection, filterConfigs]);

  // ---------------------------------------------------------------------------
  // Pagination helpers
  // ---------------------------------------------------------------------------

  const resetPagination = () => {
    setCurrentPage(1);
    setCursorStack([]);
  };

  const handleNextPage = () => {
    if (!hasNextPage || !pageInfo?.endCursor) {
      return;
    }
    setCursorStack((prev) => [...prev, pageInfo.endCursor!]);
    setCurrentPage((prev) => prev + 1);
  };

  const handlePreviousPage = () => {
    if (!hasPreviousPage) {
      return;
    }
    setCursorStack((prev) => prev.slice(0, -1));
    setCurrentPage((prev) => Math.max(1, prev - 1));
  };

  // ---------------------------------------------------------------------------
  // Event handlers
  // ---------------------------------------------------------------------------

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    resetPagination();
    debouncedUpdateSearchParam(value);
  };

  const handleTestTypeChange = (value: PesTestType | null) => {
    setSelectedTestType(value);
    resetPagination();
    updateSearchParams({ selectedTestType: value });

  };

  const handleFromDateChange = (nextFrom: Date) => {
    const adjustedTo = dateRange.to < nextFrom ? nextFrom : dateRange.to;
    const nextRange = { from: nextFrom, to: adjustedTo };
    setDateRange(nextRange);
    setFromDateSelected(true);
    if (!toDateSelected) {
      defaultToDateRef.current = nextRange.to;
    }
    resetPagination();
    updateSearchParams({ dateRange: nextRange, fromDateSelected: true });

  };

  const handleToDateChange = (nextTo: Date) => {
    const nextRange = { from: dateRange.from, to: nextTo };
    setDateRange(nextRange);
    setToDateSelected(true);
    resetPagination();
    updateSearchParams({ dateRange: nextRange, toDateSelected: true });

  };

  const handleFromDateClear = () => {
    setFromDateSelected(false);
    const fallbackFrom = getDefaultFromDate();
    const nextRange = { ...dateRange, from: fallbackFrom };
    setDateRange(nextRange);
    if (!toDateSelected) {
      defaultToDateRef.current = nextRange.to;
    }
    resetPagination();
    updateSearchParams({ dateRange: nextRange, fromDateSelected: false });

  };

  const handleToDateClear = () => {
    setToDateSelected(false);
    const fallbackTo = new Date();
    const nextRange = { ...dateRange, to: fallbackTo };
    setDateRange(nextRange);
    defaultToDateRef.current = fallbackTo;
    resetPagination();
    updateSearchParams({ dateRange: nextRange, toDateSelected: false });

  };

  const resetFilters = () => {
    const resetRange = { from: getDefaultFromDate(), to: new Date() };
    setSearchTerm("");
    setSelectedOrganisations([]);
    setSelectedFarms([]);
    setSelectedFields([]);
    setSelectedTestType(null);
    setSelectedCassettes([]);
    setSelectedErus([]);
    setSelectedTros([]);
    setDateRange(resetRange);
    setFromDateSelected(false);
    setToDateSelected(false);
    defaultToDateRef.current = resetRange.to;
    setSelectedTests([]);
    setSortKey("utcTestTime");
    setSortDirection("DESC");
    resetPagination();
    updateSearchParams({
      searchTerm: "",
      selectedOrganisations: [],
      selectedFarms: [],
      selectedFields: [],
      selectedTestType: null,
      selectedCassettes: [],
      selectedErus: [],
      selectedTros: [],
      dateRange: resetRange,
      fromDateSelected: false,
      toDateSelected: false,
      sortKey: "utcTestTime",
      sortDirection: "DESC",
    });

  };

  const handleTestSelection = (test: TestNode) => {
    setSelectedTests((prev) => {
      if (prev.some((s) => s.id === test.id)) {
        return prev.filter((s) => s.id !== test.id);
      }
      return [...prev, test];
    });
  };

  const handleCompare = () => {
    if (selectedTests.length >= 2) {
      navigate(
        `/admin/pes-tests/compare?tests=${selectedTests.map((t) => t.id).join(",")}`
      );
    } else if (selectedTests.length === 1) {
      navigate(`/admin/pes-tests/${selectedTests[0].id}`);
    }
  };

  const handleBulkMarkViewed = () => {
    setOpenedTests((prev) => {
      const updated = { ...prev };
      selectedTests.forEach((test) => {
        updated[test.id] = { openedAt: Date.now() };
      });
      return updated;
    });
  };

  const handleBulkMarkUnviewed = () => {
    setOpenedTests((prev) => {
      const updated = { ...prev };
      selectedTests.forEach((test) => {
        delete updated[test.id];
      });
      return updated;
    });
  };

  const handleBulkExcludeFromML = async () => {
    await Promise.all(
      selectedTests.map((test) =>
        updateExcludeFromML({
          variables: { testId: test.id, exclude: true },
        })
      )
    );
  };

  const handleBulkIncludeInML = async () => {
    await Promise.all(
      selectedTests.map((test) =>
        updateExcludeFromML({
          variables: { testId: test.id, exclude: false },
        })
      )
    );
  };

  const handleRowClick = (test: TestNode, event: React.MouseEvent) => {
    const target = event.target as HTMLElement;
    const cell = target.closest("td");
    if (
      cell?.dataset.key === "selection" ||
      target.closest('input[type="checkbox"]')
    ) {
      return;
    }
    setOpenedTests((prev) => ({
      ...prev,
      [test.id]: { openedAt: Date.now() },
    }));
    navigate(`/admin/pes-tests/${test.id}`);
  };

  const handleSort = (key: SortKey) => {
    const nextDirection: "ASC" | "DESC" =
      sortKey === key && sortDirection === "ASC" ? "DESC" : "ASC";
    setSortKey(key);
    setSortDirection(nextDirection);
    resetPagination();
    updateSearchParams({
      sortKey: key,
      sortDirection: nextDirection,
    });

  };

  const handlePageSizeChange = (newPageSize: PageSize) => {
    setPageSize(newPageSize);
    savePageSizeToStorage(newPageSize);
    resetPagination();
  };

  // ---------------------------------------------------------------------------
  // Derived state
  // ---------------------------------------------------------------------------

  const getActiveFiltersCount = () =>
    Number(selectedOrganisations.length > 0) +
    Number(selectedFarms.length > 0) +
    Number(selectedFields.length > 0) +
    Number(Boolean(selectedTestType)) +
    Number(Boolean(searchTerm)) +
    Number(fromDateSelected) +
    Number(toDateSelected) +
    Number(selectedCassettes.length > 0) +
    Number(selectedErus.length > 0) +
    Number(selectedTros.length > 0);

  const activeFiltersCount = getActiveFiltersCount();
  const selectedTestIds = useMemo(
    () => new Set(selectedTests.map((t) => t.id)),
    [selectedTests]
  );
  const isAllSelected =
    tests.length > 0 && tests.every((t) => selectedTestIds.has(t.id));

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const toAdd = tests.filter((t) => !selectedTestIds.has(t.id));
      setSelectedTests((prev) => [...prev, ...toAdd]);
    } else {
      setSelectedTests((prev) =>
        prev.filter((t) => !tests.some((row) => row.id === t.id))
      );
    }
  };

  const columnCount = 14;

  const renderSortIcon = (key: SortKey) => {
    if (sortKey !== key) {
      return (
        <ArrowUpDown
          className="size-4 text-muted-foreground"
          aria-hidden="true"
        />
      );
    }
    return sortDirection === "ASC" ? (
      <ArrowUp className="size-4" aria-hidden="true" />
    ) : (
      <ArrowDown className="size-4" aria-hidden="true" />
    );
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  if (error) {
    console.error("Error fetching PES tests:", error);
    return (
      <PageTemplate
        title="PES Tests"
        breadcrumbs={[
          { label: "Admin", path: "/admin" },
          { label: "PES Tests", path: "/admin/pes-tests" },
        ]}
        activeItem="Admin"
        activeSubItem="PES Tests"
      >
        <div className="rounded-md border border-destructive/30 bg-destructive/10 p-4">
          <p className="text-sm text-destructive">
            Error loading PES tests. Please try again later.
          </p>
        </div>
      </PageTemplate>
    );
  }

  return (
    <PageTemplate
      title="PES Tests"
      activeItem="Admin"
      activeSubItem="Test Files"
      breadcrumbs={[
        { label: "Admin", path: "/admin" },
        { label: "Test Files", path: "/admin/pes-tests" },
      ]}
      actions={[
        ...(selectedTests.length >= 1
          ? [
              {
                text: "Bulk Actions",
                element: (
                  <DropdownMenu>
                    <DropdownMenuTrigger>
                      <Button variant="secondary">
                        Bulk Actions ({selectedTests.length})
                        <ChevronDown className="ml-2 size-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={handleBulkMarkViewed}>
                        Mark as Viewed
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={handleBulkMarkUnviewed}>
                        Mark as Unviewed
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={handleBulkExcludeFromML}>
                        Exclude from ML
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={handleBulkIncludeInML}>
                        Include in ML
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                ),
              },
            ]
          : []),
        ...(selectedTests.length > 1
          ? [{ text: "Compare Tests", action: handleCompare }]
          : []),
      ]}
    >
      <div className="flex flex-col gap-6">
        <JobStatusCards />

        {/* Filters */}
        <Card className="overflow-hidden gap-0">
          <CardHeader className="flex flex-col gap-3 pb-0 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle className="text-base font-semibold text-foreground">
              Filters
            </CardTitle>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setCollapsed((prev) => !prev)}
              >
                <span>
                  {collapsed ? "Show filters" : "Hide filters"}
                  {activeFiltersCount > 0 ? ` (${activeFiltersCount})` : ""}
                </span>
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={resetFilters}
                disabled={
                  activeFiltersCount === 0 &&
                  !selectedTestType &&
                  searchTerm.length === 0
                }
              >
                Reset
              </Button>
            </div>
          </CardHeader>
          {!collapsed && (
            <CardContent className="grid grid-cols-1 gap-x-6 gap-y-4 pb-0 pt-6 sm:grid-cols-2 lg:grid-cols-3">
              <div className="w-full space-y-1.5">
                <FilterMultiSelect
                  id="organisation-filter"
                  label="Organisation"
                  placeholder="Select organisation..."
                  options={organisationOptions}
                  value={organisationSelections}
                  onChange={handleOrganisationChange}
                />
              </div>
              <div className="w-full space-y-1.5">
                <FilterMultiSelect
                  id="farm-filter"
                  label="Farm"
                  placeholder="Select farm..."
                  options={farmOptions}
                  value={farmSelections}
                  onChange={handleFarmChange}
                />
              </div>
              <div className="w-full space-y-1.5">
                <FilterMultiSelect
                  id="field-filter"
                  label="Field"
                  placeholder="Select field..."
                  options={fieldOptions}
                  value={fieldSelections}
                  onChange={handleFieldChange}
                />
              </div>
              <div className="w-full space-y-1.5">
                <Label
                  htmlFor="test-type-filter"
                  className="text-sm font-semibold text-foreground"
                >
                  Test Type
                </Label>
                <Select
                  value={selectedTestType ?? "all"}
                  onValueChange={(value) =>
                    handleTestTypeChange(
                      value === "all" ? null : (value as PesTestType)
                    )
                  }
                >
                  <SelectTrigger id="test-type-filter" className="w-full">
                    <SelectValue placeholder="Select test type..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All test types</SelectItem>
                    <SelectItem value={PesTestType.SoilTest}>
                      Soil Test
                    </SelectItem>
                    <SelectItem value={PesTestType.SensorCheck}>
                      Sensor Check
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="w-full space-y-1.5">
                <FilterMultiSelect
                  id="cassette-filter"
                  label="Cassette"
                  placeholder="Select cassette..."
                  options={cassetteOptions}
                  value={cassetteSelections}
                  onChange={handleCassetteChange}
                />
              </div>
              <div className="w-full space-y-1.5">
                <FilterMultiSelect
                  id="eru-filter"
                  label="ERU"
                  placeholder="Select ERU..."
                  options={eruOptions}
                  value={eruSelections}
                  onChange={handleEruChange}
                />
              </div>
              <div className="w-full space-y-1.5">
                <FilterMultiSelect
                  id="tro-filter"
                  label="TRO"
                  placeholder="Select TRO..."
                  options={troOptions}
                  value={troSelections}
                  onChange={handleTroChange}
                />
              </div>
              <div className="w-full space-y-1.5">
                <DatePickerField
                  id="from-date-filter"
                  label="From date"
                  placeholder="Select date"
                  selectedDate={dateRange.from}
                  isActive={fromDateSelected}
                  onSelect={handleFromDateChange}
                  onClear={handleFromDateClear}
                />
              </div>
              <div className="space-y-1.5">
                <DatePickerField
                  id="to-date-filter"
                  label="To date"
                  placeholder="Select date"
                  selectedDate={dateRange.to}
                  isActive={toDateSelected}
                  onSelect={handleToDateChange}
                  onClear={handleToDateClear}
                />
              </div>
              <div className="w-full space-y-2 sm:col-span-2 lg:col-span-3">
                <Label
                  htmlFor="pes-tests-search"
                  className="text-sm font-semibold text-foreground"
                >
                  Search
                </Label>
                <Input
                  id="pes-tests-search"
                  value={searchTerm}
                  onChange={(event) => handleSearch(event.target.value)}
                  placeholder="Search by name..."
                  className="h-10"
                />
              </div>
            </CardContent>
          )}
        </Card>

        {/* Table */}
        <Card className="py-0">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10 px-4 py-3">
                    <Checkbox
                      checked={isAllSelected}
                      onCheckedChange={(checked) =>
                        handleSelectAll(Boolean(checked))
                      }
                      aria-label="Select all tests in view"
                    />
                  </TableHead>
                  <TableHead className="w-10"></TableHead>
                  <TableHead className="pl-2 pr-4 py-3">
                    <Button
                      type="button"
                      variant="ghost"
                      className="ml-3 flex w-full items-center justify-between gap-2 px-0 py-0 text-left text-sm font-semibold"
                      onClick={() => handleSort("utcTestTime")}
                    >
                      Test Time
                      {renderSortIcon("utcTestTime")}
                    </Button>
                  </TableHead>
                  <TableHead className="px-4 py-3 text-sm font-semibold">
                    Uploaded At
                  </TableHead>
                  <TableHead className="px-4 py-3">
                    <Button
                      type="button"
                      variant="ghost"
                      className="flex w-full items-center justify-between gap-2 px-0 py-0 text-left text-sm font-semibold"
                      onClick={() => handleSort("name")}
                    >
                      Name
                      {renderSortIcon("name")}
                    </Button>
                  </TableHead>
                  <TableHead className="px-4 py-3 text-sm font-semibold">
                    Test Type
                  </TableHead>
                  <TableHead className="px-4 py-3 text-sm font-semibold">
                    Uploaded By
                  </TableHead>
                  <TableHead className="px-4 py-3 text-sm font-semibold">
                    Is Failure
                  </TableHead>
                  <TableHead className="px-4 py-3 text-sm font-semibold">
                    Excluded from ML
                  </TableHead>
                  <TableHead className="px-4 py-3 text-sm font-semibold">
                    Processed
                  </TableHead>
                  <TableHead className="px-4 py-3 text-sm font-semibold">
                    Soil Sample
                  </TableHead>
                  <TableHead className="px-4 py-3 text-sm font-semibold">
                    Organisation
                  </TableHead>
                  <TableHead className="px-4 py-3">
                    <Button
                      type="button"
                      variant="ghost"
                      className="flex w-full items-center justify-between gap-2 px-0 py-0 text-left text-sm font-semibold"
                      onClick={() => handleSort("farm")}
                    >
                      Farm
                      {renderSortIcon("farm")}
                    </Button>
                  </TableHead>
                  <TableHead className="px-4 py-3">
                    <Button
                      type="button"
                      variant="ghost"
                      className="flex w-full items-center justify-between gap-2 px-0 py-0 text-left text-sm font-semibold"
                      onClick={() => handleSort("field")}
                    >
                      Field
                      {renderSortIcon("field")}
                    </Button>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && !data ? (
                  Array.from({ length: Math.min(pageSize, 5) }).map(
                    (_, index) => (
                      <TableRow key={`loading-${index}`}>
                        {Array.from({ length: columnCount }).map(
                          (_, cellIndex) => (
                            <TableCell
                              key={`loading-${index}-${cellIndex}`}
                              className="h-12 px-4 py-3"
                            >
                              <Skeleton className="h-4 w-24" />
                            </TableCell>
                          )
                        )}
                      </TableRow>
                    )
                  )
                ) : tests.length > 0 ? (
                  tests.map((test) => {
                    const isSelected = selectedTestIds.has(test.id);
                    const testTypeLabel =
                      test.testType === PesTestType.SoilTest
                        ? "Soil Test"
                        : "Sensor Check";
                    return (
                      <TableRow
                        key={test.id}
                        className={cn(
                          "cursor-pointer transition-colors hover:bg-muted/70 dark:hover:bg-accent/50",
                          isSelected && "bg-primary/10 dark:bg-primary/20"
                        )}
                        onClick={(event) => handleRowClick(test, event)}
                      >
                        <TableCell
                          className="w-10 px-4 py-3"
                          data-key="selection"
                        >
                          <div
                            className="flex items-center"
                            onClick={(event) => event.stopPropagation()}
                          >
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={() => handleTestSelection(test)}
                              aria-label={`Select ${test.name}`}
                            />
                          </div>
                        </TableCell>
                        <TableCell className="px-2 py-3">
                          <DropdownMenu>
                            <DropdownMenuTrigger>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="size-8"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <MoreVertical className="size-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start">
                              <DropdownMenuItem
                                onClick={(e) => {
                                  e.stopPropagation();
                                  updateExcludeFromML({
                                    variables: {
                                      testId: test.id,
                                      exclude: !test.excludeFromMl,
                                    },
                                  });
                                }}
                              >
                                {test.excludeFromMl
                                  ? "Include in ML"
                                  : "Exclude from ML"}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (openedTests[test.id]) {
                                    setOpenedTests((prev) => {
                                      const { [test.id]: _, ...rest } = prev;
                                      return rest;
                                    });
                                  } else {
                                    setOpenedTests((prev) => ({
                                      ...prev,
                                      [test.id]: { openedAt: Date.now() },
                                    }));
                                  }
                                }}
                              >
                                {openedTests[test.id]
                                  ? "Mark as unviewed"
                                  : "Mark as viewed"}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                        <TableCell className="whitespace-nowrap pl-2 pr-4 py-3 text-sm font-medium">
                          <span className="inline-flex items-center gap-1.5">
                            <span
                              className={cn(
                                "size-1.5 rounded-full shrink-0",
                                isTestNew(test.id, test.createdAt, openedTests)
                                  ? "bg-primary animate-pulse"
                                  : "bg-transparent"
                              )}
                            />
                            {format(
                              new Date(test.utcTestTime),
                              "dd/MM/yyyy HH:mm"
                            )}
                          </span>
                        </TableCell>
                        <TableCell className="whitespace-nowrap px-4 py-3 text-sm font-medium">
                          {format(new Date(test.createdAt), "dd/MM/yyyy HH:mm")}
                        </TableCell>
                        <TableCell className="px-4 py-3 text-sm">
                          {test.name}
                        </TableCell>
                        <TableCell className="px-4 py-3 text-sm">
                          {testTypeLabel}
                        </TableCell>
                        <TableCell className="px-4 py-3 text-sm">
                          {displayUserName(test.createdBy, "unknown")}
                        </TableCell>
                        <TableCell className="px-4 py-3 text-sm">
                          {test.isFailure ? (
                            <span className="inline-flex items-center gap-1">
                              <AlertTriangle
                                className={cn(
                                  "size-4",
                                  testTypeLabel === "Soil Test"
                                    ? "text-red-500"
                                    : "text-orange-500"
                                )}
                              />
                              Yes
                            </span>
                          ) : (
                            "No"
                          )}
                        </TableCell>
                        <TableCell className="px-4 py-3 text-sm">
                          {test.excludeFromMl ? "Yes" : "No"}
                        </TableCell>
                        <TableCell className="px-4 py-3 text-sm">
                          {test.sampleFileUrl ? "Yes" : "No"}
                        </TableCell>
                        <TableCell className="px-4 py-3 text-sm">
                          {test.soilSample?.name ?? "None"}
                        </TableCell>
                        <TableCell className="px-4 py-3 text-sm">
                          {test.organisation?.name ?? "unknown"}
                        </TableCell>
                        <TableCell className="px-4 py-3 text-sm">
                          {test.farm?.name ?? "unknown"}
                        </TableCell>
                        <TableCell className="px-4 py-3 text-sm">
                          {test.field?.name ?? "unknown"}
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : null}
              </TableBody>
            </Table>
            {!loading && tests.length === 0 && (
              <div className="flex h-24 items-center justify-center text-sm text-muted-foreground">
                No tests found.
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pagination */}
        <Card>
          <CardContent className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                size="sm"
                disabled={!hasPreviousPage}
                onClick={handlePreviousPage}
              >
                Previous
              </Button>
              <span className="text-sm text-muted-foreground">
                Page {currentPage}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={!hasNextPage}
                onClick={handleNextPage}
              >
                Next
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold uppercase text-muted-foreground">
                Rows per page
              </span>
              <Select
                value={String(pageSize)}
                onValueChange={(value) =>
                  handlePageSizeChange(Number(value) as PageSize)
                }
              >
                <SelectTrigger className="h-8 w-[88px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <SelectItem key={size} value={size.toString()}>
                      {size}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
      </div>
    </PageTemplate>
  );
};

export default PesTests;
